import type { DateRange } from "react-day-picker";
import {
    endOfMonth,
    endOfWeek,
    startOfMonth,
    startOfWeek,
    sub,
    startOfDay,
    endOfDay,
    format,
} from "date-fns";
import { store } from "../../store";

export const isRangeEqual = (a: DateRange, b: DateRange): boolean => {
    return a.from?.getTime() === b.from?.getTime() && a.to?.getTime() === b.to?.getTime();
};

export const getRangeDuration = (range: DateRange): number => {
    return (range?.to?.getTime() ?? range.from?.getTime() ?? 0) - (range?.from?.getTime() ?? 0);
};

export const isRangeValid = (range: DateRange): boolean => {
    if (!range.from || !range.to) {
        return false;
    }
    if (range.from > range.to) {
        return false;
    }
    return true;
};

const startOfToday = (): Date => startOfDay(new Date());
const endOfToday = (): Date => endOfDay(new Date());
const startOfYesterday = (): Date => startOfDay(sub(new Date(), { days: 1 }));
const endOfYesterday = (): Date => endOfDay(sub(new Date(), { days: 1 }));
const startOfDayBefore = (): Date => sub(new Date(), { days: 2 });

const startOfThisWeek = (): Date =>
    startOfWeek(new Date(), {
        locale: store.i18n.languageInfo?.localeDateFns,
    });
const endOfThisWeek = (): Date =>
    endOfWeek(new Date(), {
        locale: store.i18n.languageInfo?.localeDateFns,
    });
const startOfLastWeek = (): Date =>
    startOfWeek(
        sub(new Date(), {
            weeks: 1,
        }),
        {
            locale: store.i18n.languageInfo?.localeDateFns,
        },
    );
const endOfLastWeek = (): Date =>
    endOfWeek(
        sub(new Date(), {
            weeks: 1,
        }),
        {
            locale: store.i18n.languageInfo?.localeDateFns,
        },
    );
const startOfWeekBefore = (): Date =>
    startOfWeek(
        sub(new Date(), {
            weeks: 2,
        }),
        {
            locale: store.i18n.languageInfo?.localeDateFns,
        },
    );
const startOfThisMonth = (): Date => startOfMonth(new Date());
const endOfThisMonth = (): Date => endOfMonth(new Date());
const startOfLastMonth = (): Date =>
    startOfMonth(
        sub(new Date(), {
            months: 1,
        }),
    );
const endOfLastMonth = (): Date =>
    endOfMonth(
        sub(new Date(), {
            months: 1,
        }),
    );
const startOfMonthBefore = (): Date =>
    startOfMonth(
        sub(new Date(), {
            months: 2,
        }),
    );

export const today = (): DateRange => {
    return {
        from: startOfToday(),
        to: endOfToday(),
    };
};

export const yesterday = (): DateRange => {
    return {
        from: startOfYesterday(),
        to: endOfYesterday(),
    };
};

export const thisWeek = (): DateRange => {
    return {
        from: startOfThisWeek(),
        to: endOfThisWeek(),
    };
};

export const lastWeek = (): DateRange => {
    return {
        from: startOfLastWeek(),
        to: endOfLastWeek(),
    };
};

export const thisMonth = (): DateRange => {
    return {
        from: startOfThisMonth(),
        to: endOfThisMonth(),
    };
};

export const lastMonth = (): DateRange => {
    return {
        from: startOfLastMonth(),
        to: endOfLastMonth(),
    };
};

export type PastRange = {
    start: Date;
} & (
    | {
          label:
              | "yesterday"
              | "lastWeek"
              | "lastMonth"
              | "dayBefore"
              | "weekBefore"
              | "monthBefore";
      }
    | {
          label: "custom";
          days: number;
      }
);

export const getPastRangeStart = (current: DateRange): PastRange => {
    if (isRangeEqual(current, today())) {
        return {
            start: startOfYesterday(),
            label: "yesterday",
        };
    } else if (isRangeEqual(current, yesterday())) {
        return {
            start: startOfDayBefore(),
            label: "dayBefore",
        };
    } else if (isRangeEqual(current, thisWeek())) {
        return {
            start: startOfLastWeek(),
            label: "lastWeek",
        };
    } else if (isRangeEqual(current, lastWeek())) {
        return {
            start: startOfWeekBefore(),
            label: "weekBefore",
        };
    } else if (isRangeEqual(current, thisMonth())) {
        return {
            start: startOfLastMonth(),
            label: "lastMonth",
        };
    } else if (isRangeEqual(current, lastMonth())) {
        return {
            start: startOfMonthBefore(),
            label: "monthBefore",
        };
    }

    const duration = getRangeDuration(current);
    const from = startOfDay(new Date((current.from?.getTime() ?? 0) - duration));
    return {
        start: from,
        label: "custom",
        days: Math.ceil(duration / (1000 * 60 * 60 * 24)),
    };
};

export type DateRangeQuickSelector =
    | "today"
    | "yesterday"
    | "thisWeek"
    | "lastWeek"
    | "thisMonth"
    | "lastMonth";

export const detectDateRangeQuickSelector = (
    current: DateRange,
): DateRangeQuickSelector | undefined => {
    if (isRangeEqual(current, today())) {
        return "today";
    } else if (isRangeEqual(current, yesterday())) {
        return "yesterday";
    } else if (isRangeEqual(current, thisWeek())) {
        return "thisWeek";
    } else if (isRangeEqual(current, lastWeek())) {
        return "lastWeek";
    } else if (isRangeEqual(current, thisMonth())) {
        return "thisMonth";
    } else if (isRangeEqual(current, lastMonth())) {
        return "lastMonth";
    }
    return undefined;
};

export const quickSelectorToDateRange = (quickSelector: DateRangeQuickSelector): DateRange => {
    switch (quickSelector) {
        case "today":
            return today();
        case "yesterday":
            return yesterday();
        case "thisWeek":
            return thisWeek();
        case "lastWeek":
            return lastWeek();
        case "thisMonth":
            return thisMonth();
        case "lastMonth":
            return lastMonth();
    }
};

const THREE_DAYS_MS = 72 * 3600 * 1000;

export const useTimelineFormatting = (range: DateRange): ((date: Date) => string) => {
    const diff =
        (range?.to?.getTime() ?? range.from?.getTime() ?? 0) - (range?.from?.getTime() ?? 0);
    if (diff < THREE_DAYS_MS) {
        return (date: Date): string => format(date, "HH:mm");
    }
    return (date: Date): string => format(date, "yyyy-MM-dd");
};
